import { z } from 'zod';

import type { ItemStatus, ProductDisposition, Stage } from '../types/lineage-images.api.types';

/** Every catalog stage in flow order — the one order lineage rows (and the grid columns) follow. */
export const STAGE_FLOW_ORDER = [
  'IMPORT',
  'IMAGE_GATE',
  'ARM_ASSIGNMENT',
  'CURATION',
  'FETCH',
  'PRE_ENRICH_SCORE',
  'SPLIT',
  'SAMPLE_SELECT',
  'SAMPLE',
  'ENRICHMENT',
  'POST_ENRICH_SCORE',
  'ROLLUPS',
  'INSIGHTS',
  'APPROVAL',
  'DELIVERY',
  'EXPORT',
  'TERMINAL',
] as const satisfies readonly Stage[];

/**
 * The lineage stages a person can filter by (catalog `run_items` is written by IMPORT, IMAGE_GATE
 * and CURATION; later stages live in the workflow service). The Lineage URL accepts only these, so
 * a hand-edited `?stage=ENRICHMENT` falls back to "all stages" instead of an always-empty grid.
 * IMAGE_GATE is hidden from the UI (hidden-stages.ts) — its rows fold into the Feed intake column
 * (`foldHiddenLineageCells`), so an old `?stage=IMAGE_GATE` link also falls back to "all stages".
 */
export const RUN_LINEAGE_STAGES = [
  'IMPORT',
  'CURATION',
] as const satisfies readonly (typeof STAGE_FLOW_ORDER)[number][];

export type RunLineageStage = (typeof RUN_LINEAGE_STAGES)[number];

/** Narrows a stage to one the run lineage records (the Stage filter's option set). */
export function isRunLineageStage(stage: Stage): stage is RunLineageStage {
  return (RUN_LINEAGE_STAGES as readonly Stage[]).includes(stage);
}

export const ITEM_STATUSES = [
  'SUCCESS',
  'FAILED',
  'SKIPPED',
  'REVISIT',
  'PENDING',
] as const satisfies readonly ItemStatus[];

export const PRODUCT_DISPOSITIONS = [
  'NET_NEW',
  'CHANGED',
  'REVISIT',
  'RECURATED',
  'ALREADY_CURATED',
  'REMOVED',
] as const satisfies readonly ProductDisposition[];

/** The Lineage tab's views: the listing × stage grid, or its issues grouped by reason. */
export const RUN_LINEAGE_VIEWS = ['listings', 'issues'] as const;

export type RunLineageView = (typeof RUN_LINEAGE_VIEWS)[number];

/** URL search params of a run's Lineage tab — filters and the view survive Back and are shareable. */
export const runLineageSearchSchema = z.object({
  view: z.enum(RUN_LINEAGE_VIEWS).optional().catch(undefined),
  stage: z.enum(RUN_LINEAGE_STAGES).optional().catch(undefined),
  status: z.enum(ITEM_STATUSES).optional().catch(undefined),
  disposition: z.enum(PRODUCT_DISPOSITIONS).optional().catch(undefined),
  /** Opens this listing's preview on arrival — the product page's "Open in run ↗". */
  listing: z.string().optional().catch(undefined),
});

export type RunLineageSearch = z.infer<typeof runLineageSearchSchema>;

/** Sorts stages into flow order (unknown values last) so columns never depend on arrival order. */
export function sortStagesByFlow(stages: Iterable<Stage>): Stage[] {
  const rank = (stage: Stage) => {
    const index = (STAGE_FLOW_ORDER as readonly Stage[]).indexOf(stage);
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };
  return [...new Set(stages)].sort((a, b) => rank(a) - rank(b));
}

/** Page sizes offered for the lineage walk. */
export const LINEAGE_PAGE_SIZES: readonly number[] = [50, 100];

/** First page size the lineage grid opens with. */
export const DEFAULT_LINEAGE_PAGE_SIZE = LINEAGE_PAGE_SIZES[0] ?? 50;
